import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import axiosInstance from "../../utils/axiosInstance";
import axios from "axios";

const withTokenValidation = (WrappedComponent) => {
  return (props) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const navigate = useNavigate();
    const refreshTimeoutRef = useRef(null);
    const isHandlingRefreshRef = useRef(false);

    useEffect(() => {
      const handleSessionExpiration = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        sessionStorage.setItem("sessionExpired", "true"); // Flag for "session expired" message
        setIsAuthenticated(false);
        navigate("/login");
      };

      const handleAccessTokenExpiration = async () => {
        // Prevent concurrent refresh handling in this HOC
        if (isHandlingRefreshRef.current) return;
        isHandlingRefreshRef.current = true;

        // Quick guard: if another part of the app flagged session expired, honor it
        if (sessionStorage.getItem("sessionExpired") === "true") {
          handleSessionExpiration();
          isHandlingRefreshRef.current = false;
          return;
        }

        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) {
          handleSessionExpiration();
          isHandlingRefreshRef.current = false;
          return;
        }

        try {
          // Call refresh endpoint directly (use axios to avoid axiosInstance interceptor)
          const res = await axios.post("/api/auth/refresh-token", {
            refreshToken,
          });

          const newAccessToken = res.data.accessToken;
          const newRefreshToken = res.data.refreshToken;
          localStorage.setItem("token", newAccessToken);
          localStorage.setItem("refreshToken", newRefreshToken);

          // Notify UserContext and other listeners
          window.dispatchEvent(new Event("tokenUpdated"));

          // Keep UI mounted and mark authenticated
          setIsAuthenticated(true);
        } catch (error) {
          console.error("Failed to refresh token", error);
          handleSessionExpiration();
        } finally {
          isHandlingRefreshRef.current = false;
        }
      };

      const checkTokenValidity = () => {
        // If another part of the app already flagged the session as expired, redirect immediately
        if (sessionStorage.getItem("sessionExpired") === "true") {
          handleSessionExpiration();
          return;
        }

        const token = localStorage.getItem("token");
        const refreshToken = localStorage.getItem("refreshToken");
        const currentTime = Date.now() / 1000; // Current time in seconds

        if (refreshToken) {
          try {
            const decodedRefreshToken = jwtDecode(refreshToken);

            // Check if the refresh token has expired
            if (decodedRefreshToken.exp < currentTime) {
              handleSessionExpiration();
              //console.log("Refresh token expired!");
              return;
            }
          } catch (error) {
            console.error("Invalid refresh token", error);
            handleSessionExpiration();
            return;
          }
        } else {
          // No refresh token found
          handleSessionExpiration();
          return;
        }

        if (token) {
          try {
            const decodedToken = jwtDecode(token);

            // Check if the access token has expired
            if (decodedToken.exp < currentTime) {
              //console.log("Access token expired!");
              // Attempt to refresh the token here, if needed
              handleAccessTokenExpiration();
              return;
            } else {
              setIsAuthenticated(true);
              if (
                !decodedToken.activeCompanyId &&
                window.location.pathname !== "/select-company" &&
                window.location.pathname !== "/change-password"
              ) {
                navigate("/select-company");
              }
            }
          } catch (error) {
            console.error("Invalid access token", error);
            handleSessionExpiration();
          }
        } else {
          handleSessionExpiration();
        }
      };

      // Initial token check
      checkTokenValidity();

      // Schedule proactive refresh based on token expiry and also fallback to interval
      const scheduleRefresh = () => {
        const token = localStorage.getItem("token");
        if (!token) return;
        try {
          const decoded = jwtDecode(token);
          const currentTime = Date.now() / 1000;
          // Refresh 60 seconds before expiry
          const refreshInMs = Math.max(
            0,
            (decoded.exp - currentTime - 60) * 1000
          );

          // Clear any existing timeout
          if (refreshTimeoutRef.current)
            clearTimeout(refreshTimeoutRef.current);

          refreshTimeoutRef.current = setTimeout(() => {
            checkTokenValidity();
          }, refreshInMs);
        } catch (e) {
          // If token invalid, trigger immediate check
          checkTokenValidity();
        }
      };

      // Schedule first proactive refresh and also run a periodic fallback
      scheduleRefresh();
      const interval = setInterval(checkTokenValidity, 15 * 60 * 1000);

      // Cleanup on component unmount
      return () => {
        clearInterval(interval);
        if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      };
    }, [navigate]);

    return isAuthenticated ? <WrappedComponent {...props} /> : null;
  };
};

export default withTokenValidation;
